import { Subscription } from 'rxjs';
import { Component, Input, Output, EventEmitter, OnInit, OnDestroy, OnChanges } from '@angular/core';
import { FormGroup, FormBuilder, FormArray, FormControl } from '@angular/forms';
import { PropertiesFormService } from './properties-form.service';
import { KeyValue } from '@angular/common';
import moment from 'moment';

import {
  CLEAR_SELECT_INPUT_LABEL,
  EXCLUDED_PROPERTIES,
  PROPERTIES_FORM_NAME,
  PRODUCT_PACKAGE,
  DYNAMIC_PROPERTYES
} from './properties-form.config';
import { TranslateService } from '@ngx-translate/core';
import { ProfileService } from '@features/profile/profile.service';
import { AccountService } from '@services/account.service';
import { MessageService } from '@ui-components/messages/messages.service';
import { ClonerService } from '@services/cloner.service';
import { OfferService } from '@features/profile/offer/offer.service';
import { UiComponentsService } from '@ui-components/ui-components.service';
import { VEHICLE_DATA_SOURCE } from '@config/vehicle-registry.config';

@Component({
  standalone: false,
  selector: 'ingsv-adm-properties-form',
  templateUrl: './properties-form.component.html',
  styleUrls: ['./properties-form.component.scss']
})
export class PropertiesFormComponent implements OnInit, OnChanges, OnDestroy {
  @Input() productType: any;
  @Input() productData: any;
  @Input() errorProductParameters = {};
  @Input() vehicleDataSource: string = '';

  @Output() changedPropertiesData = new EventEmitter<any>();
  @Output() changedPropertiesDataValid = new EventEmitter<any>();
  @Output() propertiesFormValid = new EventEmitter<any>();
  @Output() propertiesFormDataChanged = new EventEmitter<any>();
  @Output() propertiesFormDataInitial = new EventEmitter<any>();

  objectNameList = [];
  formData = {};
  productsConfig: any;
  currentFormProperties: any;
  objectKeys = Object.keys;

  formDataOrder: any;
  animalTypeValue = '';
  objectCategoryValue = '';
  travelExclusiveValue = '';
  formLoader = false;
  private isSyncingStormValuablesObjects = false;
  formValuesData = null;

  propertiesForm: FormGroup;
  propertiesFormSub: Subscription;
  private dynamicPropertiesSub: Subscription;
  private isApplyingDynamicDefaults = false;
  ofvVariantsListSub: Subscription;
  carAdditionalDataSub: Subscription;
  isOfvVariantsListExist = false;
  licencenceNumberEmptyFlow = false;
  forceUseCarRegisterDataValue = false;
  userConfig: any;
  private storedVehicleLicenseNumber = '';

  constructor(
    public accountService: AccountService,
    public offerService: OfferService,
    public profileService: ProfileService,
    private propertiesFormService: PropertiesFormService,
    private fb: FormBuilder,
    private translateService: TranslateService,
    private messageService: MessageService,
    private clonerService: ClonerService,
    private uiComponentsService: UiComponentsService
  ) {
    this.productsConfig = this.profileService.fetchProductsMap();
    this.formDataOrder = (a: KeyValue<string, string>, b: KeyValue<string, string>): number => {
      return a.key === PROPERTIES_FORM_NAME ? -1 : (a.key < b.key ? 1 : 0);
    };
    this.userConfig = this.accountService.getUser();
  }

  ngOnChanges(changes: any) {
    const vehicleDataSourceOnly = changes.vehicleDataSource
      && !changes.productData
      && !changes.productType
      && this.propertiesForm;

    if (vehicleDataSourceOnly) {
      this.syncVehicleRegistryFields();
      return;
    }

    if (this.productData) {
      this.productData = JSON.parse(JSON.stringify(this.productData));

      const reinitData = this.formatPropertiesInitData();

      this.initForms(reinitData[0], reinitData[1]);

      this.populateWtcDynamicDefaultProperties();
      this.populateExtendedDynamicDefaultPropertiesKolibriKlp();
      this.populateExtendedDynamicDefaultPropertiesTTFlow();
      this.syncVehicleRegistryFields();
      this.populateExtendedDynamicDefaultPropertiesStormFlow();
    }
  }

  ngOnInit() {
    this.populateProperties();
    this.propertiesFormService.clearOfvData();

    if (this.propertiesFormService.dynamicPropertiesLoaded$) {
      this.dynamicPropertiesSub = this.propertiesFormService.dynamicPropertiesLoaded$.subscribe(() => {
        this.applyDynamicDefaults();
      });
    }
  }

  ngOnDestroy() {
    this.propertiesFormService.rentalPropertyValues = {};
    if (this.propertiesFormSub) {
      this.propertiesFormSub.unsubscribe();
    }
    if (this.dynamicPropertiesSub) {
      this.dynamicPropertiesSub.unsubscribe();
    }
  }

  // Licence number EMPTY flow (Watercircles). Kolibri manual fields use vehicleRegistryMode instead.
  initLicencenceNumberEmptyFlow(init: boolean, property): void {
    if (this.isVehicleRegistryProduct()) {
      return;
    }

    this.licencenceNumberEmptyFlow = init;
    this.productData.properties.noVehicleRegNo = init;
    this.propertiesFormService.clearOfvData();
    if (init) {
      this.removeInputList(PROPERTIES_FORM_NAME, [
        DYNAMIC_PROPERTYES.OFV_VARIANTS_LIST_PROPERTY.name
      ]);

      if (this.getFormPropertyByName(DYNAMIC_PROPERTYES.VEHICLE_MODEL.name)) {
        this.removeInputList(PROPERTIES_FORM_NAME, [
          DYNAMIC_PROPERTYES.VEHICLE_MODEL.name
        ]);
      }

      if (this.getFormPropertyByName(DYNAMIC_PROPERTYES.VEHICLE_BRAND.name)) {
        this.removeInputList(PROPERTIES_FORM_NAME, [
          DYNAMIC_PROPERTYES.VEHICLE_BRAND.name
        ]);
      }

      this.propertiesFormService.disablePropertyAndClearValidators(property);
      this.propertiesFormService.clearManualFlowDataProperties(this.productData);
      this.fetchOfvCarBrands();
    } else {
      this.removeInputList(PROPERTIES_FORM_NAME, [
        DYNAMIC_PROPERTYES.OFV_CAR_BRANDS_LIST_PROPERTY.name,
        DYNAMIC_PROPERTYES.OFV_CAR_MODEL_LIST_PROPERTY.name,
        DYNAMIC_PROPERTYES.OFV_CAR_MODEL_YEAR_LIST_PROPERTY.name,
        DYNAMIC_PROPERTYES.OFV_VARIANTS_LIST_PROPERTY_WITHOUT_MANUAL_TABLE.name
      ]);
      const carRegNumber = this.propertiesFormService.getFormValueByPropertyName(this.propertiesForm, 'vehicleLicenseNumber');

      this.propertiesFormService.enableVehicleLicenceNumberProperty(property);

      if (carRegNumber) {
        this.handleOfvVariantsList(carRegNumber);
        this.productData.properties.vehicleLicenseNumber = carRegNumber;
      }
    }
  }

  fetchOfvModels(done?: any): void {
    this.formLoader = true;
    this.profileService.getCarModels(this.propertiesFormService.ofvCarData.carBrandId).subscribe({
      next: carModelsData => {
      const carModelsListPropertyConfig = DYNAMIC_PROPERTYES.OFV_CAR_MODEL_LIST_PROPERTY;

      if (carModelsData && carModelsData.length) {
        carModelsListPropertyConfig.settings.list = this.propertiesFormService.handleSelectConfig(carModelsData, 'name', 'id');

        if (this.getFormPropertyByName(DYNAMIC_PROPERTYES.OFV_CAR_MODEL_LIST_PROPERTY.name)) {
          this.removeInputList(PROPERTIES_FORM_NAME, [
            DYNAMIC_PROPERTYES.OFV_CAR_MODEL_LIST_PROPERTY.name,
            DYNAMIC_PROPERTYES.OFV_CAR_MODEL_REG_YEAR_LIST_PROPERTY.name,
            DYNAMIC_PROPERTYES.OFV_CAR_MODEL_YEAR_LIST_PROPERTY.name,
            DYNAMIC_PROPERTYES.OFV_VARIANTS_LIST_PROPERTY_WITHOUT_MANUAL_TABLE.name
          ]);
          this.propertiesFormService.ofvCarData.carModelId = '';
          this.propertiesFormService.ofvCarData.carRegModelYear = '';
          this.propertiesFormService.ofvCarData.carModelYear = '';
          this.propertiesFormService.ofvCarData.carVariantId = '';
        }

        this.addPropertyConfigData(
          carModelsListPropertyConfig.name,
          carModelsListPropertyConfig,
          this.propertiesFormService.ofvCarData.carModelId,
          PROPERTIES_FORM_NAME,
          'CAR_BRANDS'
        );

        if (typeof done === 'function') {
          done();
        }
        this.formLoader = false;
      }
    },
      error: error => {
      this.messageService.setGlobalError(error);
      this.formLoader = false;
    },
    });
  }

  fetchOfvModelRegYears(done?: any): void {
    this.formLoader = true;
    this.profileService.getCarModelRegYears(
      this.propertiesFormService.ofvCarData.carBrandId,
      this.propertiesFormService.ofvCarData.carModelId
    ).subscribe({
      next: carModelYearsData => {
      const carModelRegYearsListPropertyConfig = DYNAMIC_PROPERTYES.OFV_CAR_MODEL_REG_YEAR_LIST_PROPERTY;

      if (carModelYearsData && carModelYearsData.length) {
        carModelRegYearsListPropertyConfig.settings.list = this.propertiesFormService.handleSelectConfig(carModelYearsData, 'name', 'id');

      if (this.getFormPropertyByName(DYNAMIC_PROPERTYES.OFV_CAR_MODEL_REG_YEAR_LIST_PROPERTY.name)) {
        this.removeInputList(PROPERTIES_FORM_NAME, [
          DYNAMIC_PROPERTYES.OFV_CAR_MODEL_REG_YEAR_LIST_PROPERTY.name,
          DYNAMIC_PROPERTYES.OFV_CAR_MODEL_YEAR_LIST_PROPERTY.name,
          DYNAMIC_PROPERTYES.OFV_VARIANTS_LIST_PROPERTY_WITHOUT_MANUAL_TABLE.name
        ]);
        this.propertiesFormService.ofvCarData.carRegModelYear = '';
        this.propertiesFormService.ofvCarData.carModelYear = '';
        this.propertiesFormService.ofvCarData.carVariantId = '';
      }

        this.addPropertyConfigData(
          carModelRegYearsListPropertyConfig.name,
          carModelRegYearsListPropertyConfig,
          this.propertiesFormService.ofvCarData.carRegModelYear,
          PROPERTIES_FORM_NAME,
          'CAR_MODELS'
        );
        if (typeof done === 'function') {
          done();
        }
        this.formLoader = false;
      }
    },
      error: error => {
      this.messageService.setGlobalError(error);
      this.formLoader = false;
    },
    });
  }

  isErrorParameter(inputData): boolean {
    return this.errorProductParameters && this.errorProductParameters.hasOwnProperty(inputData.propertyName);
  }

  fetchOfvModelYears(done?: any): void {
    this.formLoader = true;
    this.profileService.getCarModelYears(
      this.propertiesFormService.ofvCarData.carBrandId,
      this.propertiesFormService.ofvCarData.carModelId,
      this.propertiesFormService.ofvCarData.carRegModelYear
    ).subscribe({
      next: carModelYearsData => {
      const carModelYearsListPropertyConfig = DYNAMIC_PROPERTYES.OFV_CAR_MODEL_YEAR_LIST_PROPERTY;

      if (carModelYearsData && carModelYearsData.length) {
        carModelYearsListPropertyConfig.settings.list = this.propertiesFormService.handleSelectConfig(carModelYearsData, 'name', 'id');

      if (this.getFormPropertyByName(DYNAMIC_PROPERTYES.OFV_CAR_MODEL_YEAR_LIST_PROPERTY.name)) {
        this.removeInputList(PROPERTIES_FORM_NAME, [
          DYNAMIC_PROPERTYES.OFV_CAR_MODEL_YEAR_LIST_PROPERTY.name,
          DYNAMIC_PROPERTYES.OFV_VARIANTS_LIST_PROPERTY_WITHOUT_MANUAL_TABLE.name
        ]);
        this.propertiesFormService.ofvCarData.carModelYear = '';
        this.propertiesFormService.ofvCarData.carVariantId = '';
      }

        this.addPropertyConfigData(
          carModelYearsListPropertyConfig.name,
          carModelYearsListPropertyConfig,
          this.propertiesFormService.ofvCarData.carModelYear,
          PROPERTIES_FORM_NAME,
          'REGISTRATION_YEAR'
        );
        if (typeof done === 'function') {
          done();
        }
        this.formLoader = false;
      }
    },
      error: error => {
      this.messageService.setGlobalError(error);
      this.formLoader = false;
    },
    });
  }

  fetchOfvModelVariant(): void {
    this.formLoader = true;
    this.profileService.getCarModelVariant(
      this.propertiesFormService.ofvCarData.carBrandId,
      this.propertiesFormService.ofvCarData.carModelId,
      this.propertiesFormService.ofvCarData.carRegModelYear,
      this.propertiesFormService.ofvCarData.carModelYear
    ).subscribe({
      next: carModelVariantsData => {
      const carOfvVariantListPropertyConfig = DYNAMIC_PROPERTYES.OFV_VARIANTS_LIST_PROPERTY_WITHOUT_MANUAL_TABLE;

      if (carModelVariantsData && carModelVariantsData.length) {
        carOfvVariantListPropertyConfig.settings.list =
          this.propertiesFormService.handleSelectConfig(carModelVariantsData, 'name', 'id');

        if (this.getFormPropertyByName(DYNAMIC_PROPERTYES.OFV_VARIANTS_LIST_PROPERTY_WITHOUT_MANUAL_TABLE.name)) {
          this.removeInputList(PROPERTIES_FORM_NAME, [
            DYNAMIC_PROPERTYES.OFV_VARIANTS_LIST_PROPERTY_WITHOUT_MANUAL_TABLE.name
          ]);
          this.propertiesFormService.ofvCarData.carVariantId = '';
        }
        this.addPropertyConfigData(
          carOfvVariantListPropertyConfig.name,
          carOfvVariantListPropertyConfig,
          this.propertiesFormService.ofvCarData.carVariantId,
          PROPERTIES_FORM_NAME,
          'CAR_MODEL_YEARS'
        );
      }
      this.formLoader = false;
    },
      error: error => {
      this.formLoader = false;
      this.messageService.setGlobalError(error);
    },
    });
  }
  // Licence number EMPTY flow

  additionalFormatForm(fieldSettings: any): void {
    // OFV additional field handler
    if (fieldSettings.ofvCarRegNumberValid) {
      this.handleOfvVariantsList(fieldSettings.value);
    } else if (fieldSettings.ofvCarRegNumberValid === false) {
      if (this.ofvVariantsListSub) {
        this.ofvVariantsListSub.unsubscribe();
      }
      if (this.isOfvVariantsListExist) {
        this.removeInputList(PROPERTIES_FORM_NAME, [DYNAMIC_PROPERTYES.OFV_VARIANTS_LIST_PROPERTY.name]);
      }
    }
    // OFV additional field handler

    if (fieldSettings.subTypeField) {
      this.productData.properties = this.propertiesFormService.setDefaultParametersData(
        this.productsConfig[this.productType].subtipeProperties[fieldSettings.value],
        this.productType,
        fieldSettings.value
      )[1];
      this.productData.defaultProperties = this.propertiesFormService.setDefaultParametersData(
        this.productsConfig[this.productType].subtipeProperties[fieldSettings.value],
        this.productType,
        fieldSettings.value
      )[1];

      this.initForms(
        this.productsConfig[this.productType].subtipeProperties[fieldSettings.value],
        this.propertiesFormService.isEditView(this.productData) ?
          [fieldSettings] :
          this.propertiesFormService.setDefaultParametersData(
            this.productsConfig[this.productType].subtipeProperties[fieldSettings.value],
            this.productType,
            fieldSettings.value
          )[0]
      );

      this.populateWtcDynamicDefaultProperties();
    }

    if (fieldSettings.isAnimalChip) {
      this.addPropertyConfigDataList(
        [DYNAMIC_PROPERTYES.ANIMAL_ID_PROPERTY]
      );
    } else if (fieldSettings.isAnimalChip === false) {
      this.removeInputList(PROPERTIES_FORM_NAME, [
        DYNAMIC_PROPERTYES.ANIMAL_ID_PROPERTY.name,
      ]);
    }

    if (fieldSettings.isAnimalUsageAreaCheck) {
      if (fieldSettings.value === 'DOG') {
        this.addAnimalUsageAreaProperty();
      } else {
        this.removeAnimalUsageAreaProperty();
      }
    }

    if (fieldSettings.isDriverBirthYearCheck) {
      if (fieldSettings.value === true || fieldSettings.value === 'true') {
        this.addDriverBirthYearProperty();
      } else {
        this.removeDriverBirthYearProperty();
      }
    }

    if (fieldSettings.isSerialNumber) {
      const propertiesLength = this.productsConfig[this.productType].properties.length;
      const propertiesConfig = Object.assign([], this.productsConfig[this.productType].properties);

      propertiesConfig[propertiesLength] = DYNAMIC_PROPERTYES.SERIAL_NUMBER_PROPERTY;
      this.initForms(propertiesConfig, [fieldSettings]);
    } else if (fieldSettings.isSerialNumber === false) {
      this.initForms(this.productsConfig[this.productType].properties, [fieldSettings]);
    }

    if (fieldSettings.isListEmpty) {
      this.removeInputList(PROPERTIES_FORM_NAME, [
        DYNAMIC_PROPERTYES.OBJECT_CATEGORY_TYPE_PROPERTY.name,
      ]);
    } else if (fieldSettings.isListEmpty === false) {
      const objectCategoryTypePropertyConfig = this.productsConfig[this.productType].properties
        .find(property => property.name === DYNAMIC_PROPERTYES.OBJECT_CATEGORY_TYPE_PROPERTY.name)
        || DYNAMIC_PROPERTYES.OBJECT_CATEGORY_TYPE_PROPERTY;

      this.addPropertyConfigDataList(
        [objectCategoryTypePropertyConfig]
      );
    }

    if (fieldSettings.showBoatHightSpeedCertificate) {
      this.addPropertyConfigDataList(
        [DYNAMIC_PROPERTYES.BOAT_HIGHT_SPEED_CERTIFICATE_PROPERTY],
        'NAUTICAL_COMPETENCE'
      );
    } else if (fieldSettings.showBoatHightSpeedCertificate === false) {
      this.removeInputList(PROPERTIES_FORM_NAME, [
        DYNAMIC_PROPERTYES.BOAT_HIGHT_SPEED_CERTIFICATE_PROPERTY.name
      ]);
    }

    if (fieldSettings.isWtcBoatBoatDamageCheck) {
      const productPackageValue = this.propertiesFormService.getFormValueByPropertyName(this.propertiesForm, 'productPackage');
      const wtcBoatMotorYearValue = this.propertiesFormService.getFormValueByPropertyName(this.propertiesForm, 'wtcBoatMotorAge');
      const motorAge = moment().diff(moment(wtcBoatMotorYearValue, 'YYYY'), 'years', false);

      if ((productPackageValue === PRODUCT_PACKAGE.KASKO || productPackageValue === PRODUCT_PACKAGE.SUPER) && motorAge < 5) {
        this.addPropertyConfigDataList(
          [DYNAMIC_PROPERTYES.BOAT_DAMAGE_PROPERTY]
        );
      } else {
        this.removeInputList(PROPERTIES_FORM_NAME, [
          DYNAMIC_PROPERTYES.BOAT_DAMAGE_PROPERTY.name
        ]);
      }
    }

    if (fieldSettings.isWtcBoatMotorInsuranceCheck) {
      const productPackageValue = this.propertiesFormService.getFormValueByPropertyName(this.propertiesForm, 'productPackage');
      const wtcBoatMotorYearValue = this.propertiesFormService.getFormValueByPropertyName(this.propertiesForm, 'wtcBoatMotorAge');
      const motorAge = moment().diff(moment(wtcBoatMotorYearValue, 'YYYY'), 'years', false);

      if ((productPackageValue === PRODUCT_PACKAGE.KASKO || productPackageValue === PRODUCT_PACKAGE.SUPER) && motorAge < 7) {
        this.addPropertyConfigDataList(
          [DYNAMIC_PROPERTYES.BOAT_MOTOR_INCURANCE_PROPERTY]
        );
      } else {
        this.removeInputList(PROPERTIES_FORM_NAME, [
          DYNAMIC_PROPERTYES.BOAT_MOTOR_INCURANCE_PROPERTY.name
        ]);
      }
    }

    if (fieldSettings.isKolibriProductPackageCheck) {
      const productPackageValue = this.propertiesFormService.getFormValueByPropertyName(this.propertiesForm, 'productPackage');

      if (productPackageValue === PRODUCT_PACKAGE.KASKO || productPackageValue === PRODUCT_PACKAGE.SUPER) {
        this.addPropertyConfigDataList(
          [DYNAMIC_PROPERTYES.BOAT_MOTOR_INCURANCE_PROPERTY_KOLIBRI]
        );
      } else {
        this.removeInputList(PROPERTIES_FORM_NAME, [
          DYNAMIC_PROPERTYES.BOAT_MOTOR_INCURANCE_PROPERTY_KOLIBRI.name
        ]);
      }
    }

    if (fieldSettings.isRentalPropertiesCheck) {
      this.propertiesFormService.populateRentalPropertyFormValues(fieldSettings);
      if (fieldSettings.value === PRODUCT_PACKAGE.SUPER || fieldSettings.value === true) {
        this.addRentalProperties();
      } else {
        this.removeRentalProperties();
      }
    }

    if (fieldSettings.isHouseDamagesCheck) {
      if (fieldSettings.value === PRODUCT_PACKAGE.SUPER) {
        this.addHouseDamagesProperties();
      } else {
        this.removeHouseDamagesProperties();
      }
    }

    if (fieldSettings.isElectricBicycleCoverageCheck) {
      if (fieldSettings.value === true || fieldSettings.value === 'true') {
        this.addElectricBicycleCoverageProperties();
      } else {
        this.removeElectricBicycleCoverageProperties();
      }
    }

    if (fieldSettings.isStormValuablesObjectCountCheck) {
      this.syncStormValuablesAdditionalObjects(fieldSettings.value);
    }

    if (fieldSettings.isStormBicycleCoverageCheck) {
      this.syncStormBicycleCoverages();
    }

    if (fieldSettings.isIncludeExtendedSumFixedEquipmentCheck) {
      if (fieldSettings.value === true || fieldSettings.value === 'true') {
        this.addTrailerExtendedSumAmountProperty();
      } else {
        this.removeTrailerExtendedSumAmountProperty();
      }
    }

    if (fieldSettings.isIncludeExtendedSumMovablesCheck) {
      if (fieldSettings.value === true || fieldSettings.value === 'true') {
        this.addTrailerExtendedSumMovablesProperty();
      } else {
        this.removeTrailerExtendedSumMovablesProperty();
      }
    }

    if (fieldSettings.isStormAnimalIdCheck) {
      if (this.showsStormAnimalId(fieldSettings.value)) {
        this.addStormAnimalIdProperty();
      } else {
        this.removeInputList(PROPERTIES_FORM_NAME, [
          DYNAMIC_PROPERTYES.ANIMAL_ID_PROPERTY.name
        ]);
      }
    }

    if (fieldSettings.isStormAnimalBreedCheck) {
      if (fieldSettings.value === 'PUREBRED') {
        this.addStormAnimalBreedProperty();
      } else {
        this.removeInputList(PROPERTIES_FORM_NAME, ['animalBreed']);
      }
    }

    if (fieldSettings.isStormTravelNumberOfInsuredPersonsCheck) {
      if (fieldSettings.value === 'FAMILY') {
        this.addStormTravelNumberOfInsuredPersonsProperty();
      } else {
        this.removeStormTravelNumberOfInsuredPersonsProperty();
      }
    }

    if (fieldSettings.isStormAnimalDeathSumCheck) {
      if (fieldSettings.value === true || fieldSettings.value === 'true') {
        this.addStormAnimalDeathSumProperty();
      } else {
        this.removeAnimalDeathSumProperty();
      }
    }

    if (fieldSettings.isAnimalDeathCoverageCheck) {
      if (fieldSettings.value === true || fieldSettings.value === 'true') {
        this.addAnimalDeathSumProperty();
      } else {
        this.removeAnimalDeathSumProperty();
      }
    }

    if (fieldSettings.isBoatSecuremarkCheck) {
      if (fieldSettings.value) {
        this.addPropertyConfigDataList(
          [DYNAMIC_PROPERTYES.SECURE_MARK_NUMBER_PROPERTY]
        );
      } else {
        if (this.propertiesFormService.isPropertyListAdded(this.propertiesForm, [DYNAMIC_PROPERTYES.SECURE_MARK_NUMBER_PROPERTY])) {
          this.removeInputList(PROPERTIES_FORM_NAME, [
            DYNAMIC_PROPERTYES.SECURE_MARK_NUMBER_PROPERTY.name
          ]);
        }
      }
    }

    if (fieldSettings.isBoatSecuremarkCheckKLP) {
      if (fieldSettings.value) {
        this.addPropertyConfigDataList(
          [DYNAMIC_PROPERTYES.SECURE_MARK_BOAT_PROPERTY, DYNAMIC_PROPERTYES.SECURE_MARK_BOAT_ENGINE_PROPERTY],
          'WTC_BOAT_SECUREMARK'
        );
      } else {
        this.removeInputList(PROPERTIES_FORM_NAME, [
          DYNAMIC_PROPERTYES.SECURE_MARK_BOAT_PROPERTY.name,
          DYNAMIC_PROPERTYES.SECURE_MARK_BOAT_ENGINE_PROPERTY.name
        ]);
      }
    }

    if (fieldSettings.isLicenceNumberAvailabilityCheck) {
      if (fieldSettings.value) {
        this.addPropertyConfigDataList(
          [DYNAMIC_PROPERTYES.LICENCE_NUMBER_TRAILER_HOME_PROPERTY],
          'VEHICLE_LICENCE_NUMBER_AVAILABILITY'
        );
      } else {
        this.removeInputList(PROPERTIES_FORM_NAME, [
          DYNAMIC_PROPERTYES.LICENCE_NUMBER_TRAILER_HOME_PROPERTY.name
        ]);
      }
    }

    if (fieldSettings.isRentalPeriodCheck) {
      if (fieldSettings.value) {
        this.addPropertyConfigData(
          DYNAMIC_PROPERTYES.RENTAL_PERIOD_PROPERTY.name,
          DYNAMIC_PROPERTYES.RENTAL_PERIOD_PROPERTY,
          '',
          PROPERTIES_FORM_NAME,
          'HOUSE_IS_RENTED'
        );
      } else {
        this.removeInputList(PROPERTIES_FORM_NAME, [
          DYNAMIC_PROPERTYES.RENTAL_PERIOD_PROPERTY.name
        ]);
      }
    }

    if (fieldSettings.isContinuedHealthInsuranceCheck) {
      if (fieldSettings.value) {
        this.addPropertyConfigDataList(
          [DYNAMIC_PROPERTYES.CONTINUED_INSURANCE_B2B_PROPERTY, DYNAMIC_PROPERTYES.HEALTH_SENIORITY_DATE_PROPERTY],
          'CONTINUED_INSURANCE'
        );
      } else {
        this.removeInputList(PROPERTIES_FORM_NAME, [
          DYNAMIC_PROPERTYES.CONTINUED_INSURANCE_B2B_PROPERTY.name,
          DYNAMIC_PROPERTYES.HEALTH_SENIORITY_DATE_PROPERTY.name
        ]);
      }
    }

    if (fieldSettings.isLisingCheck) {
      if (fieldSettings.value) {
        this.addPropertyConfigData(
          DYNAMIC_PROPERTYES.LISING_PROPERTY.name,
          DYNAMIC_PROPERTYES.LISING_PROPERTY,
          '',
          PROPERTIES_FORM_NAME,
          'VEHICLE_LEASING'
        );
      } else {
        this.removeInputList(PROPERTIES_FORM_NAME, [
          DYNAMIC_PROPERTYES.LISING_PROPERTY.name
        ]);
      }
    }

    if (fieldSettings.isBoatHasMotorCheck) {
      if (fieldSettings.value) {
        this.addMotorBoatProperties();
      } else {
        this.removeInputList(PROPERTIES_FORM_NAME, [
          DYNAMIC_PROPERTYES.BOAT_MOTOR_MARK_PROPERTY.name,
          DYNAMIC_PROPERTYES.BOAT_TOTAL_HP_PROPERTY.name,
          DYNAMIC_PROPERTYES.BOAT_MOTOR_AGE_PROPERTY.name
        ]);
      }
    }

    if (fieldSettings.isDriverAndPassengerCheck) {
      const productPackageValue = this.propertiesFormService.getFormValueByPropertyName(this.propertiesForm, 'productPackage');

      if (productPackageValue === PRODUCT_PACKAGE.FIRE_AND_THEFT_STANDALONE) {
        this.propertiesFormService.disableProperty(this.getFormPropertyByName('vehicleDriverAccident'));
        this.propertiesFormService.setPropertyValue(this.getFormPropertyByName('vehicleDriverAccident'), this.productData, false);
      } else {
        this.propertiesFormService.enableProperty(this.getFormPropertyByName('vehicleDriverAccident'));
      }
    }

    if (fieldSettings.isFirstRiskSumInsuredCheck) {
      if (fieldSettings.value === 'FIRST_RISK') {
        this.addPropertyConfigDataList(
          [DYNAMIC_PROPERTYES.HOUSE_FIRST_RISK_SUM_INSURED],
          fieldSettings.key || 'SUM_INSURED_TYPE',
          '',
          fieldSettings.formArrayKeyName
        );
      } else {
        this.removeFirstRiskSumProperties(fieldSettings);
      }
    }

    this.setCustomSelectValueConfig(fieldSettings);
  }

  getSectionTitle(formKey: string): string {
    // TODO: Find better way to translate titles

    let title = '';
    let additionalArrayIndex: any;
    let titleKey = formKey;
    const isAdditionalArrayTitle = this.isAdditionalArray(titleKey);
    let additionalArrayDesc = '';

    if (isAdditionalArrayTitle) {
      additionalArrayIndex = +titleKey.substring(titleKey.indexOf('_') + 1) + 1;

      if (formKey.indexOf('houseAdditionalBuildings') > -1) {
        additionalArrayDesc = this.fetchHouseAdditionalBuildingDescription(formKey, additionalArrayIndex);
      } else {
        additionalArrayDesc = additionalArrayIndex;
      }

      titleKey = titleKey.substring(0, titleKey.indexOf('_'));
    }

    title = this.translateService.instant(`PROPERTIES_FORM.TITLES.${titleKey + ''.toUpperCase()}`).indexOf('PROPERTIES_FORM') > -1 ?
      titleKey : this.translateService.instant(`PROPERTIES_FORM.TITLES.${titleKey + ''.toUpperCase()}`);

    return isAdditionalArrayTitle ? `${title} ${additionalArrayDesc}` : title;
  }

  isAdditionalArray(titleKey: string): boolean {
    return titleKey.indexOf('_') > -1;
  }

  initForms(
    propertiesData = this.productsConfig[this.productType].properties,
    formValuesDataToSet = []) {
    this.propertiesForm = this.initForm();

    if (this.productsConfig[this.productType].LIST_OF_OBJECTS) {
      this.objectNameList = this.productsConfig[this.productType].LIST_OF_OBJECTS;
    }

    propertiesData.forEach((propertyConfig, index) => {
      let formValueToSet: any = '';
      const propertyKey = propertyConfig.name;
      const valueToSetData = formValuesDataToSet.find(x => x.name === propertyKey);

      if (valueToSetData) {
        formValueToSet = valueToSetData.value;
      }

      if (propertyConfig && propertyConfig.inputType) {
        this.formatPropertyConfig(propertyConfig, propertyKey);

        const inputData = {
          propertyName: propertyKey,
          propertyConfig: propertyConfig
        };

        this.addInput(inputData, this.getPropertyValue(formValueToSet, propertyKey));
      }

      if (this.objectNameList.includes(propertyKey)) {
        const listArray = this.productData.properties ? this.productData.properties[propertyKey] : [];

        if (listArray && listArray.length) {
          listArray.forEach((additionalPropertyObject, additionalPropertyObjectIndex) => {
            propertyConfig.values.forEach(objectValue => {
              const additionalPropertyKey = objectValue.name;
              const propertyValue = listArray[additionalPropertyObjectIndex][additionalPropertyKey];

              if (objectValue.inputType) {
                if (
                  objectValue.renderOnlyIfPresent &&
                  !listArray[additionalPropertyObjectIndex].hasOwnProperty(additionalPropertyKey)
                ) {
                  return;
                }

                objectValue.index = additionalPropertyObjectIndex;

                const inputData = {
                  index: additionalPropertyObjectIndex,
                  propertyArrayName: propertyKey,
                  propertyName: additionalPropertyKey,
                  propertyConfig: objectValue
                };

                this.addInput(inputData, propertyValue, `${propertyKey}_${additionalPropertyObjectIndex}`);
              }
            });
          });
        }
      }

    });
    this.propertiesFormDataInitial.emit(this.propertiesForm.value);
    this.propertiesFormDataChanged.emit(this.propertiesForm.value);
    this.populateProperties();

    this.propertiesFormSub = this.propertiesForm
      .valueChanges
      .subscribe(form => {
        this.formValuesData = form;
        this.propertiesFormDataChanged.emit(form);
        this.populateProperties();
        this.applyDynamicDefaults();
      });

    this.applyDynamicDefaults();
  }

  addAdditionalArrayItem(key: string, firstItem = false): void {
    const propertyName = key.substring(0, key.indexOf('_'));
    const propertyIndex = firstItem ? 0 : Number(+key.substring(key.indexOf('_') + 1) + 1);
    const valuesList = this.productsConfig[this.productType].properties.find(x => x.name === propertyName);

    this.setControl(`${propertyName}_${propertyIndex}`);
    valuesList.values.forEach(objectValue => {
      const additionalPropertyKey = objectValue.name;
      let valueToSet = null;

      if (objectValue.inputType) {
        objectValue.index = propertyIndex;

        const inputData = {
          index: propertyIndex,
          propertyArrayName: propertyName,
          propertyName: additionalPropertyKey,
          propertyConfig: objectValue
        };

        if (this.propertiesFormService.defaultPropertiesConfig && this.propertiesFormService.defaultPropertiesConfig[this.productType]) {
          const defaultConfigObject = this.propertiesFormService.defaultPropertiesConfig[this.productType][propertyName];

          if (defaultConfigObject) {
            if (!this.productData.properties[propertyName]) {
              this.productData.properties[propertyName] = [];
            }

            if (!this.productData.properties[propertyName][propertyIndex]) {
              this.productData.properties[propertyName][propertyIndex] = {};
            }

            this.productData.properties[propertyName][propertyIndex].defaultProperties = defaultConfigObject;

            valueToSet = defaultConfigObject[additionalPropertyKey];
          }
        }

        this.addInput(inputData, valueToSet, `${propertyName}_${propertyIndex}`);
      }
    });
  }

  removeAdditionalArrayItem(controlName: string): void {
    const propertyArrayName = this.propertiesFormService.fetchPropertyArrayName(controlName);

    delete this.formData[controlName];
    this.propertiesForm.removeControl(controlName);

    // removeControl above re-emits and re-adds the item by index, so splice it
    // out afterwards and re-emit the compacted product. Emitting productData
    // directly avoids formatProperties re-adding it by index.
    this.propertiesFormService.removeAdditionalArrayProperties(
      propertyArrayName,
      this.propertiesFormService.fetchIndexFromFormName(controlName),
      this.productData
    );

    if (propertyArrayName === 'houseWithContents') {
      this.syncStormBicycleCoverages();
    }

    this.changedPropertiesData.emit(this.productData);
    if (this.propertiesForm.valid) {
      this.changedPropertiesDataValid.emit(this.productData);
    }
  }

  isAdditionalItemExist(itemName: string): any {
    return (this.productData.properties && this.productData.properties[itemName] && this.productData.properties[itemName].length) ||
      Object.keys(this.propertiesForm.controls).find(x => x.indexOf(itemName) > -1);
  }

  isListOfObjectsExist(): boolean {
    return this.productsConfig[this.productType].LIST_OF_OBJECTS && this.productsConfig[this.productType].LIST_OF_OBJECTS.length;
  }

  showsStormBicycleCoverageAdd(): boolean {
    return this.hasStormBicycleCoverage()
      && this.isStormBicycleSuperPackage()
      && !this.isAdditionalItemExist('bicycleCoverages');
  }

  handleUseCarRegisterData(forceUseCarRegisterData: boolean): void {
    if (forceUseCarRegisterData) {
      this.handleOfvVariantsList(
        this.propertiesFormService.getFormValueByPropertyName(this.propertiesForm, 'vehicleLicenseNumber'),
        '',
        forceUseCarRegisterData
      );
    } else {
      this.handleOfvVariantsList(this.propertiesFormService.getFormValueByPropertyName(this.propertiesForm, 'vehicleLicenseNumber'));
    }
  }

  private fetchHouseAdditionalBuildingDescription(formKey: string, additionalArrayIndex: number): string {
    const houseBuildingType = this.propertiesForm.get(formKey)['controls']
      .find(control => control.value.inputData.propertyName === 'houseBuildingType');
    
    const houseSquareMeters = this.propertiesForm.get(formKey)['controls']
      .find(control => control.value.inputData.propertyName === 'houseSquareMeters');
    
    const houseBuildingTypeValue = houseBuildingType ? houseBuildingType.value.value : '';
    const houseSquareMetersValue = houseSquareMeters ? houseSquareMeters.value.value : '';

    if (houseBuildingTypeValue) {
      return this.propertiesFormService.fetchHouseAdditionalBuildingDescription(houseBuildingTypeValue, houseSquareMetersValue);
    }

    return additionalArrayIndex.toString();
  }

  private fetchOfvCarBrands(done?: any): void {
    this.formLoader = true;
    this.profileService.getCarBrands().subscribe({
      next: carBrandsData => {
      const carBrandsListPropertyConfig = DYNAMIC_PROPERTYES.OFV_CAR_BRANDS_LIST_PROPERTY;

      if (carBrandsData && carBrandsData.length) {
        carBrandsListPropertyConfig.settings.list = this.propertiesFormService.handleSelectConfig(carBrandsData, 'name', 'id');

        this.addPropertyConfigData(
          carBrandsListPropertyConfig.name,
          carBrandsListPropertyConfig,
          this.propertiesFormService.ofvCarData.carBrandId,
          PROPERTIES_FORM_NAME,
          'LICENSE_NUMBER'
        );

        if (typeof done === 'function') {
          done();
        }
        this.formLoader = false;
      }
    },
      error: error => {
      this.formLoader = false;
      this.messageService.setGlobalError(error);
    },
    });
  }

  private addInput(inputData, propertyValue, formArrayName = PROPERTIES_FORM_NAME, addAfterKey?: string): void {
    const items = this.propertiesForm.get(formArrayName) as FormArray;

    if (!EXCLUDED_PROPERTIES.includes(inputData.propertyName)) {
      if ((inputData.propertyConfig.showIfValueExist)) {
        if (propertyValue != null) {
          this.addToInputs(items, inputData, propertyValue, addAfterKey);
        }
      } else {
        this.addToInputs(items, inputData, propertyValue, addAfterKey);
      }
    }
  }

  private addToInputs(items, inputData, propertyValue, addAfterKey): void {
    if (addAfterKey) {
      const index = items.value.findIndex(item => item.inputData.propertyConfig.key === addAfterKey);

      items.insert(index + 1, this.createInput(inputData, propertyValue));
    } else {
      items.push(this.createInput(inputData, propertyValue));
    }
  }

  private syncVehicleRegistryFields(): void {
    if (!this.isVehicleRegistryProduct() || !this.propertiesForm) {
      return;
    }

    const productConfig = this.productsConfig[this.productType];
    const mode = this.vehicleDataSource || null;
    const registryProperties = (productConfig.properties || [])
      .filter(property => property.vehicleDataSourceModes)
      .sort((left, right) => (left.vehicleRegistryOrder || 0) - (right.vehicleRegistryOrder || 0));
    let addAfterKey = 'LICENSE_NUMBER';

    registryProperties.forEach(propertyConfig => {
      const shouldShow = !!mode && propertyConfig.vehicleDataSourceModes.includes(mode);

      if (shouldShow) {
        if (!this.getFormPropertyByName(propertyConfig.name)) {
          const inputConfig = this.clonerService.deepClone({
            ...propertyConfig,
            ...propertyConfig.vehicleRegistryInput
          });
          this.addPropertyConfigData(
            propertyConfig.name,
            inputConfig,
            '',
            PROPERTIES_FORM_NAME,
            addAfterKey
          );
        }
        addAfterKey = propertyConfig.key;
      } else if (this.getFormPropertyByName(propertyConfig.name)) {
        this.removeFormInput(propertyConfig.name);
      }
    });

    this.syncVehicleLicenseNumber(mode);
    this.orderVehicleRegistryFields(mode);
    this.populateProperties();
  }

  private orderVehicleRegistryFields(mode: string | null): void {
    if (!mode) {
      return;
    }

    const items = this.propertiesForm.get(PROPERTIES_FORM_NAME) as FormArray;
    const registryNames = (this.productsConfig[this.productType].properties || [])
      .filter(property => property.vehicleDataSourceModes && property.vehicleDataSourceModes.includes(mode))
      .sort((left, right) => (left.vehicleRegistryOrder || 0) - (right.vehicleRegistryOrder || 0))
      .map(property => property.name);
    const identifier = mode === VEHICLE_DATA_SOURCE.NO_VEHICLE_REG_NO
      ? 'tffVehicleChassisNumber'
      : 'vehicleLicenseNumber';
    const leadingNames = [
      identifier,
      ...registryNames.filter(name => name !== identifier),
      'calculatedClientAge'
    ];
    const leadingControls = leadingNames
      .map(name => items.controls.find(control => control.value.inputData.propertyName === name))
      .filter(control => !!control);

    leadingControls
      .map(control => items.controls.indexOf(control))
      .sort((left, right) => right - left)
      .forEach(index => items.removeAt(index, { emitEvent: false }));

    leadingControls.forEach((control, index) => items.insert(index, control, { emitEvent: false }));
    items.controls.forEach((control, index) => {
      const propertyConfig = control.value.inputData.propertyConfig;
      control.value.inputData.propertyConfig = { ...propertyConfig, formSortingIndex: index };
    });
  }

  private isVehicleRegistryProduct(): boolean {
    return !!this.productsConfig?.[this.productType]?.vehicleRegistryMode;
  }

  private syncVehicleLicenseNumber(mode: string | null): void {
    if (!this.productData.properties) {
      this.productData.properties = {};
    }

    if (mode === VEHICLE_DATA_SOURCE.NO_VEHICLE_REG_NO) {
      this.hideVehicleLicenseNumber();
      return;
    }

    this.showVehicleLicenseNumber();
  }

  private hideVehicleLicenseNumber(): void {
    const licenseControl = this.getFormPropertyByName('vehicleLicenseNumber');

    if (licenseControl) {
      const currentValue = licenseControl.get('value').value;
      if (currentValue) {
        this.storedVehicleLicenseNumber = currentValue;
      }
      this.removeFormInput('vehicleLicenseNumber');
    }

    this.productData.properties.vehicleLicenseNumber = null;
  }

  private showVehicleLicenseNumber(): void {
    if (this.getFormPropertyByName('vehicleLicenseNumber')) {
      return;
    }

    const licenseConfig = (this.productsConfig[this.productType].properties || [])
      .find(property => property.name === 'vehicleLicenseNumber');

    if (!licenseConfig) {
      return;
    }

    const value = this.storedVehicleLicenseNumber || this.productData.properties.vehicleLicenseNumber || '';
    this.productData.properties.vehicleLicenseNumber = value;
    this.addPropertyConfigData(
      licenseConfig.name,
      this.clonerService.deepClone(licenseConfig),
      value,
      PROPERTIES_FORM_NAME,
      'MISSING_KEY_INSERT_FIRST'
    );
  }

  private removeFormInput(propertyName: string): void {
    const items = this.propertiesForm.get(PROPERTIES_FORM_NAME) as FormArray;
    const index = items.controls.findIndex(control => control.value.inputData.propertyName === propertyName);

    if (index > -1) {
      items.removeAt(index);
    }
  }

  private removeInputList(formArrayName = PROPERTIES_FORM_NAME, inputListName = []): void {
    const items = this.propertiesForm.get(formArrayName) as FormArray;

    inputListName.forEach(itemToDelete => {
      items.controls.forEach((control, index) => {
        if (control.value.inputData.propertyName === itemToDelete) {
          if (this.productData.properties && this.productData.properties.hasOwnProperty(itemToDelete)) {
            delete this.productData.properties[itemToDelete];
          }
          items.removeAt(index);
        }
      });
    });
  }

  private setCustomSelectValueConfig(fieldSettings): void {
    const items = this.propertiesForm.get(PROPERTIES_FORM_NAME) as FormArray;

    if (fieldSettings.propertyToEditName) {
      items.controls.forEach(control => {
        if (control.value.inputData.propertyName === fieldSettings.propertyToEditName) {

          if (fieldSettings.value === true) {
            fieldSettings.value = 'true';
          } else if (fieldSettings.value === 'false') {
            fieldSettings.value = 'false';
          }

          control.setValue({
            inputData: control.value.inputData,
            value: CLEAR_SELECT_INPUT_LABEL
          });

          Object.assign(
            control.value.inputData.propertyConfig,
            control.value.inputData.propertyConfig.customValueConfig[fieldSettings.value]
          );

          control.setValue({
            inputData: control.value.inputData,
            value: ''
          });
        }
      });
    }

    if (fieldSettings.needSearchSelectComponentRefresh) {
      setTimeout(() => {
        this.uiComponentsService.updateDropdownSearchComponentList$.next(undefined);
      });
    }
  }

  private addPropertyConfigData(propertyKey, propertyConfig, formValueToSet, formArrayName?: any, addAfterKey?: string): void {
    const inputData: any = {
      propertyName: propertyKey,
      propertyConfig: propertyConfig
    };

    if (this.propertiesFormService.isArrayForm(formArrayName)) {
      inputData.index = this.propertiesFormService.fetchIndexFromFormName(formArrayName);
      inputData.propertyArrayName = this.propertiesFormService.fetchPropertyArrayName(formArrayName);
    }

    let propertyValue;

    if (inputData.propertyArrayName !== undefined) {
      const arrayItem = this.productData.properties &&
        this.productData.properties[inputData.propertyArrayName] &&
        this.productData.properties[inputData.propertyArrayName][inputData.index];

      propertyValue = arrayItem && arrayItem.hasOwnProperty(propertyKey)
        ? arrayItem[propertyKey]
        : (formValueToSet || '');
    } else {
      propertyValue = this.getPropertyValue(formValueToSet, propertyKey);
    }

    this.addInput(inputData, propertyValue, formArrayName, addAfterKey);
  }

  private handleOfvVariantsList(carRegNumber: string, valueToSet = '', forceUseCarRegisterData = false): void {
    if (this.ofvVariantsListSub) {
      this.ofvVariantsListSub.unsubscribe();
    }

    this.forceUseCarRegisterDataValue = forceUseCarRegisterData;
    this.productData.properties.forceUseCarRegisterData = forceUseCarRegisterData;
    this.formLoader = true;

    this.ofvVariantsListSub = this.profileService.getOfvVariantsList(carRegNumber, forceUseCarRegisterData).subscribe({
      next: ofvData => {
      const ofvVariantListPropertyConfig = DYNAMIC_PROPERTYES.OFV_VARIANTS_LIST_PROPERTY;

      const vehicleModelConfig = DYNAMIC_PROPERTYES.VEHICLE_MODEL;
      const vehicleBrandConfig = DYNAMIC_PROPERTYES.VEHICLE_BRAND;

      if (this.getFormPropertyByName(DYNAMIC_PROPERTYES.VEHICLE_MODEL.name)) {
        this.removeInputList(PROPERTIES_FORM_NAME, [
          DYNAMIC_PROPERTYES.VEHICLE_MODEL.name
        ]);
      }

      if (this.getFormPropertyByName(DYNAMIC_PROPERTYES.VEHICLE_BRAND.name)) {
        this.removeInputList(PROPERTIES_FORM_NAME, [
          DYNAMIC_PROPERTYES.VEHICLE_BRAND.name
        ]);
      }

      if (this.getFormPropertyByName(ofvVariantListPropertyConfig.name)) {
        this.removeInputList(PROPERTIES_FORM_NAME, [
          ofvVariantListPropertyConfig.name
        ]);
      }

      ofvVariantListPropertyConfig.settings.list = this.propertiesFormService.handleSelectConfig(ofvData.variants, 'name', 'id');
      this.isOfvVariantsListExist = true;

      if (ofvVariantListPropertyConfig.settings.list.length === 1) {
        valueToSet = ofvVariantListPropertyConfig.settings.list[0].value;
      }

      this.addPropertyConfigData(vehicleModelConfig.name, vehicleModelConfig, ofvData.modelName, PROPERTIES_FORM_NAME, 'LICENSE_NUMBER');
      this.addPropertyConfigData(vehicleBrandConfig.name, vehicleBrandConfig, ofvData.brandName, PROPERTIES_FORM_NAME, 'LICENSE_NUMBER');
      this.addPropertyConfigData(ofvVariantListPropertyConfig.name, ofvVariantListPropertyConfig, valueToSet, PROPERTIES_FORM_NAME, 'TFF_VEHICLE_MODEL');

      this.productData.properties.vehicleVariantsSource = ofvData.source;

      this.formLoader = false;
    },
      error: err => {
      this.messageService.setGlobalError(err);
      this.formLoader = false;
    },
    });
  }

  private getPropertyValue(formValueToSet, propertyKey): any {
    let propertyValue: any;

    if (this.productData.properties) {
      if (formValueToSet || formValueToSet === false) {
        propertyValue = this.formatFormValues(
          this.productData.properties,
          propertyKey,
          formValueToSet
        );
      } else {
        propertyValue = this.formatFormValues(
          this.productData.properties,
          propertyKey,
          this.productData.properties[propertyKey]
        );
      }
    } else {
      if (this.currentFormProperties && this.currentFormProperties.properties && this.currentFormProperties.properties[propertyKey]) {
        propertyValue = formValueToSet ? formValueToSet : this.currentFormProperties.properties[propertyKey];
      } else {
        propertyValue = formValueToSet ? formValueToSet : '';
      }
    }

    return propertyValue;
  }

  private formatPropertiesInitData(): any {
    let parametersSetList = [];
    const propertiesConfig = this.clonerService.deepClone(this.productsConfig[this.productType].properties);

    if (!this.propertiesFormService.isEditView(this.productData) && this.userConfig.isDefaultProperties) {
      [parametersSetList, this.productData.properties] =
        this.propertiesFormService.setDefaultParametersData(propertiesConfig, this.productType);
      this.productData.defaultProperties = this.propertiesFormService.setDefaultParametersData(propertiesConfig, this.productType)[1];
    }

    if (this.userConfig.isWtcFlow) {
      return [this.handleWtcInitFormConfig(propertiesConfig), parametersSetList];
    }

    if ((this.userConfig.isTTFlow || this.userConfig.isStormFlow)) {
      this.handleTTInitFormConfig(propertiesConfig);
    }

    return [propertiesConfig, parametersSetList];
  }

  private addFormPropertyToConfig(propertiesConfig, propertyConfig, addAfterKey = ''): void {
    if (this.productData.properties) {
      const listOfObjects = this.productsConfig[this.productType].LIST_OF_OBJECTS;

      if (this.productData.properties.hasOwnProperty(propertyConfig.name)) {
        if (addAfterKey) {
          const index = propertiesConfig.findIndex(config => config.key === addAfterKey);

          propertiesConfig.splice(index + 1, 0, propertyConfig);
        } else {
          const existingConfig = propertiesConfig.find(config => config.name === propertyConfig.name);

          if (!existingConfig) {
            propertiesConfig.push(propertyConfig);
          } else if (!existingConfig.inputType) {
            Object.assign(existingConfig, propertyConfig);
          }
        }
      }

      if (listOfObjects) {
        listOfObjects.forEach(formArrayPropertName => {
          if (this.productData.properties[formArrayPropertName] && this.productData.properties[formArrayPropertName].length) {
            this.productData.properties[formArrayPropertName].forEach(formArrayPropertyItem => {
              if (formArrayPropertyItem.hasOwnProperty(propertyConfig.name)) {
                const configItem = propertiesConfig.find(config => config.name === formArrayPropertName);
                const isConfigItemValuesExist = configItem.values.find(value => value.name === propertyConfig.name);

                if (!isConfigItemValuesExist) {
                  configItem.values.push(propertyConfig);
                } else if (!isConfigItemValuesExist.inputType) {
                  Object.assign(isConfigItemValuesExist, propertyConfig);
                }

              }
            });
          }
        });
      }
    }
  }

  private formatPropertyConfig(propertyConfig: any, propertyKey: string): any {
    if (propertyKey === 'animalBreed' && this.animalTypeValue) {
      Object.assign(
        propertyConfig,
        propertyConfig.customValueConfig[this.animalTypeValue]
      );
    }

    if (propertyKey === 'objectCategoryType' && this.objectCategoryValue) {
      Object.assign(
        propertyConfig,
        propertyConfig.customValueConfig[this.objectCategoryValue]
      );
    }

    if (propertyKey === 'travelDuration' && this.travelExclusiveValue) {
      Object.assign(
        propertyConfig,
        propertyConfig.customValueConfig[this.travelExclusiveValue]
      );
    }
  }

  private formatFormValues(properties: any, propertyKey: string, value: any): any {
    let valueToSet: any = '';

    switch (propertyKey) {
      case 'vehicleBonus': {
        if (properties['vehicleBonusYears'] && properties['vehicleBonus']) {
          valueToSet = `${value},${properties['vehicleBonusYears']}`;
        } else {
          valueToSet = value ? value + '' : '';
        }
        break;
      }

      case 'animalType': {
        valueToSet = value;
        this.animalTypeValue = valueToSet;
        break;
      }
      case 'objectCategory': {
        valueToSet = value;
        this.objectCategoryValue = valueToSet;
        break;
      }
      case 'travelExclusive': {
        valueToSet = value;
        this.travelExclusiveValue = valueToSet;
        break;
      }
      default: {
        valueToSet = value;
        break;
      }
    }

    return valueToSet;
  }

  private applyDynamicDefaults(): void {
    if (this.isApplyingDynamicDefaults || !this.productType || !this.productData || !this.propertiesForm) {
      return;
    }

    if (typeof this.propertiesFormService.applyDynamicDefaultProperties !== 'function') {
      return;
    }

    const applied = this.propertiesFormService.applyDynamicDefaultProperties(
      this.productType,
      this.productData,
      this.productsConfig?.[this.productType]?.properties || [],
      !!this.propertiesFormService.isEditView(this.productData)
    );

    this.isApplyingDynamicDefaults = true;
    try {
      Object.keys(applied).forEach(propertyName => {
        const valueControl = this.getFormPropertyByName(propertyName)?.get('value');

        if (valueControl && valueControl.value !== applied[propertyName]) {
          valueControl.setValue(applied[propertyName]);
        }
      });
    } finally {
      this.isApplyingDynamicDefaults = false;
    }
  }

  private populateProperties(): void {
    const formatPropertiesData = this.propertiesFormService.formatProperties(this.propertiesForm, this.productData);
    this.propertiesFormValid.emit(this.propertiesForm.valid);
    this.changedPropertiesData.emit(
      formatPropertiesData
    );
    if (this.propertiesForm.valid) {
      this.changedPropertiesDataValid.emit(
        formatPropertiesData
      );
    }
  }

  private setControl(controlName: string): void {
    this.propertiesForm.setControl(controlName, this.fb.array([]));
    this.formData[controlName] = this.fb.array([]);
  }

  private initForm(): any {
    this.formData = {
      properties: this.fb.array([]),
    };

    if (this.productData.productType && this.productsConfig[this.productData.productType].LIST_OF_OBJECTS) {
      this.productsConfig[this.productData.productType].LIST_OF_OBJECTS.forEach((obj) => {
        if (this.productData.properties[obj] && this.productData.properties[obj].length) {
          this.productData.properties[obj].forEach((objData, objIndex) => {
            this.formData[`${obj}_${objIndex}`] = this.fb.array([]);
          });
        }
      });
    }

    return this.fb.group(this.formData);
  }

  private createInput(inputData: any, propertyValue: any): FormGroup {
    inputData.propertyConfig.validator[0] = propertyValue;
    return this.fb.group({
      inputData: [inputData],
      value: inputData.propertyConfig.validator
    });
  }

  private getFormPropertyByName(propertyName): any {
    const propertiesItems = this.propertiesForm.get(PROPERTIES_FORM_NAME) as FormArray;

    return propertiesItems.controls.find(control => control.value.inputData.propertyName === propertyName);
  }

  private removeFirstRiskSumProperties(fieldSettings): void {
    if (
      this.propertiesFormService.isPropertyListAdded(
        this.propertiesForm, [DYNAMIC_PROPERTYES.HOUSE_FIRST_RISK_SUM_INSURED], fieldSettings.formArrayKeyName
      )
    ) {
      this.removeInputList(fieldSettings.formArrayKeyName, [DYNAMIC_PROPERTYES.HOUSE_FIRST_RISK_SUM_INSURED.name]);

      if (this.productData.properties) {
        if (!fieldSettings.propertyArrayName) {
          delete this.productData.properties[DYNAMIC_PROPERTYES.HOUSE_FIRST_RISK_SUM_INSURED.name];
        } else if (this.productData.properties[fieldSettings.propertyArrayName]) {
          delete this.productData.properties[fieldSettings.propertyArrayName]
            [fieldSettings.index][DYNAMIC_PROPERTYES.HOUSE_FIRST_RISK_SUM_INSURED.name];
        }
      }

    }
  }

  private addRentalProperties(): void {
    this.addPropertyConfigDataList(
      [
        DYNAMIC_PROPERTYES.WTC_RENTAL_NUMBER_APARTMENTS_PROPERTY,
        DYNAMIC_PROPERTYES.WTC_MONTHLY_RENTAL_INCOME_PROPERTY
      ],
      '',
      '',
      PROPERTIES_FORM_NAME,
      !this.propertiesFormService.isPropertyListAdded(
        this.propertiesForm,
        [
          DYNAMIC_PROPERTYES.WTC_RENTAL_NUMBER_APARTMENTS_PROPERTY,
          DYNAMIC_PROPERTYES.WTC_MONTHLY_RENTAL_INCOME_PROPERTY
        ]
      ) &&
      this.propertiesFormService.isAllowToAddRentalProperties()
    );
  }

  private addHouseDamagesProperties(): void {
    this.addPropertyConfigDataList(
      [
        DYNAMIC_PROPERTYES.MOISURE_OR_WATER_DAMAGE_PROPERTY,
        DYNAMIC_PROPERTYES.DEFICIENCIES_OR_ERROR_OR_DAMAGES_PROPERTY
      ],
      'PRODUCT_PACKAGE',
    );
  }

  private removeHouseDamagesProperties(): void {
    this.removePropertyConfigDataList(
      [
        DYNAMIC_PROPERTYES.MOISURE_OR_WATER_DAMAGE_PROPERTY,
        DYNAMIC_PROPERTYES.DEFICIENCIES_OR_ERROR_OR_DAMAGES_PROPERTY
      ]
    );
  }

  private addElectricBicycleCoverageProperties(): void {
    this.addPropertyConfigDataList(
      [
        DYNAMIC_PROPERTYES.ELECTRIC_BICYCLE_VALUE_PROPERTY,
        DYNAMIC_PROPERTYES.ELECTRIC_BICYCLE_COVERAGE_PACKAGE_PROPERTY
      ],
      'ADD_ELECTRIC_BICYCLE_COVERAGE'
    );
  }

  private syncStormValuablesAdditionalObjects(countValue: string | number): void {
    if (this.isSyncingStormValuablesObjects || !this.propertiesForm) {
      return;
    }

    const count = Number(countValue);
    const desiredCount = Number.isInteger(count) && count > 1 ? count - 1 : 0;
    const propertyName = 'valuablesAdditionalObjects';
    const currentCount = Object.keys(this.propertiesForm.controls)
      .filter(name => name.indexOf(`${propertyName}_`) === 0)
      .length;

    if (currentCount === desiredCount) {
      return;
    }

    this.isSyncingStormValuablesObjects = true;

    try {
      if (currentCount < desiredCount) {
        for (let index = currentCount; index < desiredCount; index++) {
          if (index === 0) {
            this.addAdditionalArrayItem(`${propertyName}_0`, true);
          } else {
            this.addAdditionalArrayItem(`${propertyName}_${index - 1}`);
          }
        }
      } else {
        for (let index = currentCount - 1; index >= desiredCount; index--) {
          this.removeAdditionalArrayItem(`${propertyName}_${index}`);
        }
      }
    } finally {
      this.isSyncingStormValuablesObjects = false;
    }
  }

  private syncStormBicycleCoverages(): void {
    if (!this.hasStormBicycleCoverage() || !this.propertiesForm || this.isStormBicycleSuperPackage()) {
      return;
    }

    this.clearStormBicycleCoverages();
  }

  private clearStormBicycleCoverages(): void {
    const controlNames = Object.keys(this.propertiesForm.controls)
      .filter(name => name.indexOf('bicycleCoverages_') === 0)
      .sort((left, right) =>
        this.propertiesFormService.fetchIndexFromFormName(right) -
        this.propertiesFormService.fetchIndexFromFormName(left)
      );

    controlNames.forEach(name => this.removeAdditionalArrayItem(name));

    if (this.productData?.properties) {
      this.productData.properties.bicycleCoverages = [];
    }
  }

  private hasStormBicycleCoverage(): boolean {
    return (this.productsConfig?.[this.productType]?.properties || [])
      .some(config =>
        config.isStormBicycleCoverageCheck ||
        (config.values || []).some(value => value.isStormBicycleCoverageCheck)
      );
  }

  private isStormBicycleSuperPackage(): boolean {
    return Object.keys(this.propertiesForm?.controls || {}).some(formKey =>
      (this.propertiesForm.get(formKey) as any)?.controls?.some(control =>
        control.value?.inputData?.propertyConfig?.isStormBicycleCoverageCheck &&
        control.value?.value === PRODUCT_PACKAGE.SUPER
      )
    );
  }

  private removeElectricBicycleCoverageProperties(): void {
    this.removePropertyConfigDataList(
      [
        DYNAMIC_PROPERTYES.ELECTRIC_BICYCLE_VALUE_PROPERTY,
        DYNAMIC_PROPERTYES.ELECTRIC_BICYCLE_COVERAGE_PACKAGE_PROPERTY
      ]
    );
  }

  private addTrailerExtendedSumAmountProperty(): void {
    this.addPropertyConfigDataList(
      [this.getExtendedSumAmountProperty()],
      'INCLUDE_EXTENDED_SUM_FIXED_EQUIPMENT'
    );
  }

  private removeTrailerExtendedSumAmountProperty(): void {
    this.removePropertyConfigDataList([this.getExtendedSumAmountProperty()]);
  }

  private addTrailerExtendedSumMovablesProperty(): void {
    this.addPropertyConfigDataList(
      [this.getExtendedSumMovablesProperty()],
      'INCLUDE_EXTENDED_SUM_MOVABLES'
    );
  }

  private removeTrailerExtendedSumMovablesProperty(): void {
    this.removePropertyConfigDataList([this.getExtendedSumMovablesProperty()]);
  }

  private getExtendedSumAmountProperty() {
    return this.getConfiguredSelectProperty('contentsSumInsured')
      || (this.productType === 'MOBILE_HOME'
        ? DYNAMIC_PROPERTYES.MOBILE_HOME_EXTENDED_SUM_AMOUNT_PROPERTY
        : DYNAMIC_PROPERTYES.TRAILER_EXTENDED_SUM_AMOUNT_PROPERTY);
  }

  private getExtendedSumMovablesProperty() {
    return this.getConfiguredSelectProperty('productSumInsured')
      || (this.productType === 'MOBILE_HOME'
        ? DYNAMIC_PROPERTYES.MOBILE_HOME_EXTENDED_SUM_MOVABLES_PROPERTY
        : DYNAMIC_PROPERTYES.TRAILER_EXTENDED_SUM_MOVABLES_PROPERTY);
  }

  private getConfiguredSelectProperty(propertyName: string) {
    const productProperty = (this.productsConfig?.[this.productType]?.properties || [])
      .find(config => config.name === propertyName && config.settings?.list);

    if (!productProperty) {
      return null;
    }

    return {
      ...productProperty,
      inputType: 'select_data_list'
    };
  }

  private addAnimalDeathSumProperty(): void {
    this.addPropertyConfigDataList(
      [DYNAMIC_PROPERTYES.ANIMAL_DEATH_SUM_PROPERTY],
      'ANIMAL_DEATH_COVERAGE'
    );
  }

  private addStormAnimalDeathSumProperty(): void {
    const productProperty = (this.productsConfig?.[this.productType]?.properties || [])
      .find(config => config.name === 'animalSIDeath');

    if (!productProperty) {
      return;
    }

    this.addPropertyConfigDataList(
      [{
        ...productProperty,
        inputType: 'number'
      }],
      'ANIMAL_DEATH_COVERAGE'
    );
  }

  private showsStormAnimalId(value): boolean {
    return value === 'LIVESTOCK_ID_MARKING' || value === 'LIVESTOCK_MEMBER_NKK';
  }

  private addStormAnimalIdProperty(): void {
    this.addPropertyConfigDataList(
      [DYNAMIC_PROPERTYES.ANIMAL_ID_PROPERTY],
      'ANIMAL_LIVESTOCK_MARKING_MEMBERSHIP'
    );
  }

  private addStormTravelNumberOfInsuredPersonsProperty(): void {
    this.addPropertyConfigDataList(
      [DYNAMIC_PROPERTYES.TRAVEL_NUMBER_OF_INSURED_PERSONS_PROPERTY],
      'TRAVEL_TYPE'
    );
  }

  private removeStormTravelNumberOfInsuredPersonsProperty(): void {
    this.removePropertyConfigDataList([
      DYNAMIC_PROPERTYES.TRAVEL_NUMBER_OF_INSURED_PERSONS_PROPERTY
    ]);
  }

  private addStormAnimalBreedProperty(): void {
    const source = (this.productsConfig?.[this.productType]?.properties || [])
      .find(config => config.name === 'animalBreed');

    if (!source) {
      return;
    }

    const items = this.propertiesForm.get(PROPERTIES_FORM_NAME) as FormArray;
    const animalType = items?.controls
      .find(control => control.value.inputData.propertyName === 'animalType')?.value?.value
      || this.animalTypeValue;
    const property = {
      ...source,
      inputType: 'select_search_data_list'
    };

    if (animalType && property.customValueConfig?.[animalType]) {
      Object.assign(property, property.customValueConfig[animalType]);
    }

    this.addPropertyConfigDataList([property], 'ANIMAL_BREED_TYPE');
  }

  private addHouseFirstRiskSumInsuredProperty(): void {
    this.addPropertyConfigDataList(
      [DYNAMIC_PROPERTYES.HOUSE_FIRST_RISK_SUM_INSURED],
      'SUM_INSURED_TYPE'
    );
  }

  private removeAnimalDeathSumProperty(): void {
    this.removePropertyConfigDataList([DYNAMIC_PROPERTYES.ANIMAL_DEATH_SUM_PROPERTY]);
  }

  private addAnimalUsageAreaProperty(): void {
    this.addPropertyConfigDataList(
      [DYNAMIC_PROPERTYES.ANIMAL_USAGE_AREA_PROPERTY],
      'ANIMAL_TYPE'
    );
  }

  private removeAnimalUsageAreaProperty(): void {
    this.removePropertyConfigDataList([DYNAMIC_PROPERTYES.ANIMAL_USAGE_AREA_PROPERTY]);
  }

  private addDriverBirthYearProperty(): void {
    this.addPropertyConfigDataList(
      [DYNAMIC_PROPERTYES.DRIVER_BIRTH_YEAR_PROPERTY],
      'YOUNG_DRIVER_OTHER'
    );
  }

  private removeDriverBirthYearProperty(): void {
    this.removePropertyConfigDataList([DYNAMIC_PROPERTYES.DRIVER_BIRTH_YEAR_PROPERTY]);
  }

  private isSnowmobileOrTractorProductType(): boolean {
    return this.productType === 'SNOWMOBILE' || this.productType === 'TRACTOR';
  }

  private removeRentalProperties(): void {
    this.removePropertyConfigDataList(
      [
        DYNAMIC_PROPERTYES.WTC_RENTAL_NUMBER_APARTMENTS_PROPERTY,
        DYNAMIC_PROPERTYES.WTC_MONTHLY_RENTAL_INCOME_PROPERTY
      ]
    );
  }

  private addMotorBoatProperties(): void {
    this.addPropertyConfigDataList(
      [
        DYNAMIC_PROPERTYES.BOAT_MOTOR_MARK_PROPERTY,
        DYNAMIC_PROPERTYES.BOAT_TOTAL_HP_PROPERTY,
        DYNAMIC_PROPERTYES.BOAT_MOTOR_AGE_PROPERTY
      ],
      'BOAT_HAS_MOTOR',
    );
  }

  private addPropertyConfigDataList(
    propertyList,
    addAfterKey = '',
    formValueToSet = '',
    formArrayName = PROPERTIES_FORM_NAME,
    allowAddProperties = !this.propertiesFormService.isPropertyListAdded(
      this.propertiesForm,
      propertyList,
      formArrayName
    )
  ): void {
    if (allowAddProperties) {
      propertyList.forEach(property => {
        this.addPropertyConfigData(
          property.name,
          property,
          formValueToSet,
          formArrayName,
          addAfterKey
        );
      });
    }
  }

  private removePropertyConfigDataList(
    propertyList,
    formArrayName = PROPERTIES_FORM_NAME,
    allowRemoveProperties = this.propertiesFormService.isPropertyListAdded(
      this.propertiesForm,
      propertyList,
      formArrayName
    )
  ): void {
    if (allowRemoveProperties) {
      this.removeInputList(formArrayName, propertyList.map(property => property.name));
    }

    if (this.productData.properties) {
      propertyList.forEach(property => {
        delete this.productData.properties[property.name];
      });
    }
  }

  // Wtc custom dynamic properties logic
  private populateWtcDynamicDefaultProperties(): void {
    if (this.userConfig.isWtcFlow && this.productData.properties) {
      if (this.productData.properties.wtcBoatHasMotor) {
        this.addMotorBoatProperties();
      }

      if (this.productData.properties.vehicleLicenceNumberAvailability) {
        this.addPropertyConfigData(
          DYNAMIC_PROPERTYES.LICENCE_NUMBER_TRAILER_HOME_PROPERTY.name,
          DYNAMIC_PROPERTYES.LICENCE_NUMBER_TRAILER_HOME_PROPERTY, '',
          PROPERTIES_FORM_NAME,
          'VEHICLE_LICENCE_NUMBER_AVAILABILITY'
        );
      }

      if (this.productData.properties.wtcBoatSecuremark) {
        this.addPropertyConfigDataList(
          [DYNAMIC_PROPERTYES.SECURE_MARK_NUMBER_PROPERTY],
          'WTC_BOAT_SECUREMARK',
        );
      }

      if (this.productType === 'TRACTOR' || this.productType === 'SNOWMOBILE') {
        if (this.productData.properties.productPackage === PRODUCT_PACKAGE.FIRE_AND_THEFT_STANDALONE) {
          this.propertiesFormService.disableProperty(this.getFormPropertyByName('vehicleDriverAccident'));
        }
      }
    }
  }

  private handleWtcInitFormConfig(propertiesConfig): void {
    if (this.productType === 'VALUABLES') {
      const wtcValuablesObjectTypeValue = this.productData.properties && this.productData.properties.wtcValuablesObjectType;
      const wtcValuablesObjectTypeConfig = propertiesConfig.find(x => x.name === 'wtcValuablesObjectType');

      if (wtcValuablesObjectTypeValue) {
        const isSerialNumber = wtcValuablesObjectTypeConfig
          .settings.list.find(x => x.value === wtcValuablesObjectTypeValue).isSerialNumber;

        if (isSerialNumber) {
          propertiesConfig.push(DYNAMIC_PROPERTYES.SERIAL_NUMBER_PROPERTY);
        }
      }
    }

    if (this.productType === 'ANIMAL') {
      this.addFormPropertyToConfig(propertiesConfig, DYNAMIC_PROPERTYES.ANIMAL_ID_PROPERTY);
    }

    if ((this.productType === 'CAR' || this.productType === 'MOTORCYCLE') && !this.isVehicleRegistryProduct()) {
      if (!this.propertiesFormService.isManualCarCreationFlow(this.productData.properties)) {
        if (this.productData.properties.vehicleLicenseNumber) {
          this.handleOfvVariantsList(
            this.productData.properties.vehicleLicenseNumber,
            this.productData.properties.ofvVariantId,
            this.productData.properties.forceUseCarRegisterData
          );
        }
      } else {
        this.licencenceNumberEmptyFlow = true;
        this.propertiesFormService.populateInitOfvData(this.productData.properties);

        this.fetchOfvCarBrands(doneCarBrands => {
          this.propertiesFormService.populateInitOfvData(this.productData.properties);
          this.fetchOfvModels(doneCarModels => {
            this.fetchOfvModelRegYears(doneCarRegModelsYears => {
              this.fetchOfvModelYears(doneCarModelsYears => {
                this.fetchOfvModelVariant();
              });
            });
          });
        });
        setTimeout(() =>
          this.propertiesFormService.disablePropertyAndClearValidators(this.getFormPropertyByName('vehicleLicenseNumber')));
      }
    }

    if (this.productType === 'HOUSE') {
      this.propertiesFormService.populateRentalPropertyValues(this.productData.properties);
      this.addFormPropertyToConfig(propertiesConfig, DYNAMIC_PROPERTYES.WTC_RENTAL_NUMBER_APARTMENTS_PROPERTY);
      this.addFormPropertyToConfig(propertiesConfig, DYNAMIC_PROPERTYES.WTC_MONTHLY_RENTAL_INCOME_PROPERTY);
      this.addFormPropertyToConfig(propertiesConfig, DYNAMIC_PROPERTYES.HOUSE_FIRST_RISK_SUM_INSURED);
    }

    if (this.productType === 'MOBILE_HOME') {
      this.addFormPropertyToConfig(propertiesConfig, DYNAMIC_PROPERTYES.LISING_PROPERTY, 'VEHICLE_LEASING');
    }

    if (this.productType === 'HEALTH') {
      this.addFormPropertyToConfig(propertiesConfig, DYNAMIC_PROPERTYES.CONTINUED_INSURANCE_B2B_PROPERTY, 'CONTINUED_INSURANCE');
      this.addFormPropertyToConfig(propertiesConfig, DYNAMIC_PROPERTYES.HEALTH_SENIORITY_DATE_PROPERTY, 'CONTINUED_INSURANCE');
    }

    if (this.productType === 'VACATION_HOME') {
      this.addFormPropertyToConfig(propertiesConfig, DYNAMIC_PROPERTYES.HOUSE_FIRST_RISK_SUM_INSURED);
    }

    if (this.productType === 'BOAT') {
      const boatGroup = this.productData.properties && this.productData.properties.wtcBoatGroup;

      if (boatGroup) {
        propertiesConfig = Object.assign([], this.productsConfig[this.productType].subtipeProperties[boatGroup]);

        this.addFormPropertyToConfig(propertiesConfig, DYNAMIC_PROPERTYES.SECURE_MARK_NUMBER_PROPERTY, 'WTC_BOAT_SECUREMARK');
        this.addFormPropertyToConfig(propertiesConfig, DYNAMIC_PROPERTYES.BOAT_HIGHT_SPEED_CERTIFICATE_PROPERTY, 'NAUTICAL_COMPETENCE');
        this.addFormPropertyToConfig(propertiesConfig, DYNAMIC_PROPERTYES.BOAT_MOTOR_INCURANCE_PROPERTY);

        if (boatGroup !== 'MotorBoat') {
          // TODO: CHECK
          this.addFormPropertyToConfig(propertiesConfig, DYNAMIC_PROPERTYES.BOAT_MOTOR_MARK_PROPERTY, 'BOAT_HAS_MOTOR');
          this.addFormPropertyToConfig(propertiesConfig, DYNAMIC_PROPERTYES.BOAT_TOTAL_HP_PROPERTY, 'BOAT_HAS_MOTOR');
          this.addFormPropertyToConfig(propertiesConfig, DYNAMIC_PROPERTYES.BOAT_MOTOR_AGE_PROPERTY, 'BOAT_HAS_MOTOR');
        }
      }
    }

    return propertiesConfig;
  }

  private isHouseOrVacationHomeProductType(): boolean {
    return this.productType === 'HOUSE' || this.productType === 'VACATION_HOME';
  }

  private handleTTInitFormConfig(propertiesConfig): void {
    if (this.isHouseOrVacationHomeProductType()) {
      this.addFormPropertyToConfig(propertiesConfig, DYNAMIC_PROPERTYES.HOUSE_FIRST_RISK_SUM_INSURED);
    }
  }

  // Extended properties TTFlow dynamic properties logic
  private populateExtendedDynamicDefaultPropertiesTTFlow(): void {
    if (this.userConfig.isTTFlow && this.productData.properties) {
      if (this.productType === 'ANIMAL') {
        if (
          this.productData.properties.hasOwnProperty(DYNAMIC_PROPERTYES.ANIMAL_DEATH_SUM_PROPERTY.name) ||
          this.productData.properties.animalDeathCoverage === true ||
          this.productData.properties.animalDeathCoverage === 'true'
        ) {
          this.addAnimalDeathSumProperty();
        }
      }

      if (this.productType === 'CONTENTS') {
        if (
          this.productData.properties.hasOwnProperty(DYNAMIC_PROPERTYES.ELECTRIC_BICYCLE_VALUE_PROPERTY.name) ||
          this.productData.properties.addElectricBicycleCoverage === true ||
          this.productData.properties.addElectricBicycleCoverage === 'true'
        ) {
          this.addElectricBicycleCoverageProperties();
        }
      }

      if (this.productType === 'TRAILER' || this.productType === 'TRAILER_HOME' || this.productType === 'MOBILE_HOME') {
        if (
          this.productData.properties.hasOwnProperty(DYNAMIC_PROPERTYES.TRAILER_EXTENDED_SUM_AMOUNT_PROPERTY.name) ||
          this.productData.properties.includeExtendedSumFixedEquipment === true ||
          this.productData.properties.includeExtendedSumFixedEquipment === 'true'
        ) {
          this.addTrailerExtendedSumAmountProperty();
        }

        if (
          this.productData.properties.hasOwnProperty(DYNAMIC_PROPERTYES.TRAILER_EXTENDED_SUM_MOVABLES_PROPERTY.name) ||
          this.productData.properties.includeExtendedSumMovables === true ||
          this.productData.properties.includeExtendedSumMovables === 'true'
        ) {
          this.addTrailerExtendedSumMovablesProperty();
        }
      }
    }
  }

  private populateExtendedDynamicDefaultPropertiesStormFlow(): void {
    if (this.userConfig.isStormFlow && this.productData.properties) {
      if (this.productType === 'ANIMAL') {
        const animalPropertyConfigs = this.productsConfig?.[this.productType]?.properties || [];

        if (animalPropertyConfigs.some(config => config.isStormAnimalDeathSumCheck)) {
          if (
            this.productData.properties.hasOwnProperty('animalSIDeath') ||
            this.productData.properties.animalDeathCoverage === true ||
            this.productData.properties.animalDeathCoverage === 'true'
          ) {
            this.addStormAnimalDeathSumProperty();
          }
        }

        if (animalPropertyConfigs.some(config => config.isStormAnimalBreedCheck)) {
          if (
            this.productData.properties.hasOwnProperty('animalBreed') ||
            this.productData.properties.animalBreedType === 'PUREBRED'
          ) {
            this.addStormAnimalBreedProperty();
          }
        }

        if (animalPropertyConfigs.some(config => config.isStormAnimalIdCheck)) {
          const membership = this.productData.properties.animalLivestockMarkingMembership;

          if (
            this.productData.properties.hasOwnProperty('animalId') ||
            this.showsStormAnimalId(membership)
          ) {
            this.addStormAnimalIdProperty();
          }
        }
      }

      if (this.hasStormBicycleCoverage()) {
        this.syncStormBicycleCoverages();
      }

      if (this.productType === 'VALUABLES') {
        const valuablesPropertyConfigs = this.productsConfig?.[this.productType]?.properties || [];
        const hasObjectCountCheck = valuablesPropertyConfigs
          .some(config => config.isStormValuablesObjectCountCheck);

        if (hasObjectCountCheck) {
          this.syncStormValuablesAdditionalObjects(this.productData.properties.numberOfInsuredObjects);
        }
      }

      if (this.productType === 'TRAILER' || this.productType === 'TRAILER_HOME' || this.productType === 'MOBILE_HOME') {
        if (
          this.productData.properties.hasOwnProperty(DYNAMIC_PROPERTYES.TRAILER_EXTENDED_SUM_AMOUNT_PROPERTY.name) ||
          this.productData.properties.includeExtendedSumFixedEquipment === true ||
          this.productData.properties.includeExtendedSumFixedEquipment === 'true'
        ) {
          this.addTrailerExtendedSumAmountProperty();
        }

        if (
          this.productData.properties.hasOwnProperty(DYNAMIC_PROPERTYES.TRAILER_EXTENDED_SUM_MOVABLES_PROPERTY.name) ||
          this.productData.properties.includeExtendedSumMovables === true ||
          this.productData.properties.includeExtendedSumMovables === 'true'
        ) {
          this.addTrailerExtendedSumMovablesProperty();
        }
      }

      if (this.productType === 'TRACTOR') {
        if (
          this.productData.properties.hasOwnProperty('contentsSumInsured') ||
          this.productData.properties.includeExtendedSumFixedEquipment === true ||
          this.productData.properties.includeExtendedSumFixedEquipment === 'true'
        ) {
          this.addTrailerExtendedSumAmountProperty();
        }
      }

      if (this.productType === 'TRAVEL') {
        const travelPropertyConfigs = this.productsConfig?.[this.productType]?.properties || [];

        if (travelPropertyConfigs.some(config => config.isStormTravelNumberOfInsuredPersonsCheck)) {
          if (
            this.productData.properties.hasOwnProperty('travelNumberOfInsuredPersons') ||
            this.productData.properties.travelType === 'FAMILY'
          ) {
            this.addStormTravelNumberOfInsuredPersonsProperty();
          }
        }
      }
    }
  }

  // Extended properties flow dynamic properties logic
  private populateExtendedDynamicDefaultPropertiesKolibriKlp(): void {
    if ((this.userConfig.isKlpFlow || this.userConfig.isKolibriFlow) && this.productData.properties) {
      const objectCategoryTypeValue = this.productData.properties.objectCategoryType;

      if (!objectCategoryTypeValue) {
        this.removeInputList(PROPERTIES_FORM_NAME, [DYNAMIC_PROPERTYES.OBJECT_CATEGORY_TYPE_PROPERTY.name]);
      }

      if (this.productData.properties.hasOwnProperty(DYNAMIC_PROPERTYES.RENTAL_PERIOD_PROPERTY.name)) {
        this.addPropertyConfigData(
          DYNAMIC_PROPERTYES.RENTAL_PERIOD_PROPERTY.name,
          DYNAMIC_PROPERTYES.RENTAL_PERIOD_PROPERTY,
          this.productData.properties[DYNAMIC_PROPERTYES.RENTAL_PERIOD_PROPERTY.name],
          PROPERTIES_FORM_NAME,
          'HOUSE_IS_RENTED'
        );
      }

      if (
        this.productData.properties.hasOwnProperty(DYNAMIC_PROPERTYES.SECURE_MARK_BOAT_PROPERTY.name) ||
        (this.productData.properties.hasOwnProperty('wtcBoatSecuremark') && this.productData.properties.wtcBoatSecuremark)
      ) {
        this.addPropertyConfigData(
          DYNAMIC_PROPERTYES.SECURE_MARK_BOAT_PROPERTY.name,
          DYNAMIC_PROPERTYES.SECURE_MARK_BOAT_PROPERTY,
          this.productData.properties[DYNAMIC_PROPERTYES.SECURE_MARK_BOAT_PROPERTY.name],
          PROPERTIES_FORM_NAME,
          'WTC_BOAT_SECUREMARK'
        );
      }

      if (
        this.productData.properties.hasOwnProperty(DYNAMIC_PROPERTYES.SECURE_MARK_BOAT_ENGINE_PROPERTY.name) ||
        (this.productData.properties.hasOwnProperty('wtcBoatSecuremark') && this.productData.properties.wtcBoatSecuremark)
      ) {
        this.addPropertyConfigData(
          DYNAMIC_PROPERTYES.SECURE_MARK_BOAT_ENGINE_PROPERTY.name,
          DYNAMIC_PROPERTYES.SECURE_MARK_BOAT_ENGINE_PROPERTY,
          this.productData.properties[DYNAMIC_PROPERTYES.SECURE_MARK_BOAT_ENGINE_PROPERTY.name],
          PROPERTIES_FORM_NAME,
          'WTC_BOAT_SECUREMARK'
        );
      }

      if (
        this.productType === 'BOAT' &&
        (this.productData.properties.productPackage === PRODUCT_PACKAGE.KASKO ||
          this.productData.properties.productPackage === PRODUCT_PACKAGE.SUPER) &&
        !this.propertiesFormService.isPropertyListAdded(this.propertiesForm, [
          DYNAMIC_PROPERTYES.BOAT_MOTOR_INCURANCE_PROPERTY_KOLIBRI
        ])
      ) {
        this.addPropertyConfigData(
          DYNAMIC_PROPERTYES.BOAT_MOTOR_INCURANCE_PROPERTY_KOLIBRI.name,
          DYNAMIC_PROPERTYES.BOAT_MOTOR_INCURANCE_PROPERTY_KOLIBRI,
          this.productData.properties[DYNAMIC_PROPERTYES.BOAT_MOTOR_INCURANCE_PROPERTY_KOLIBRI.name],
          PROPERTIES_FORM_NAME
        );
      }

      if (this.productType === 'CAR' && this.userConfig.isKlpFlow) {
        if (this.productData.properties.vehicleLicenseNumber) {
          this.handleOfvVariantsList(
            this.productData.properties.vehicleLicenseNumber,
            this.productData.properties.ofvVariantId,
            this.productData.properties.forceUseCarRegisterData
          );
        }
      }

      if (
        this.productData.properties.hasOwnProperty(DYNAMIC_PROPERTYES.MOISURE_OR_WATER_DAMAGE_PROPERTY.name) &&
        this.productData.properties.hasOwnProperty(DYNAMIC_PROPERTYES.DEFICIENCIES_OR_ERROR_OR_DAMAGES_PROPERTY.name)
      ) {
        this.addHouseDamagesProperties();
      } else if (this.isHouseOrVacationHomeProductType()) {
          if (
            this.productData.properties.hasOwnProperty('productPackage') &&
            this.productData.properties.productPackage === PRODUCT_PACKAGE.SUPER
          ) {
            this.addHouseDamagesProperties();
        }
      }

      if (this.productType === 'ANIMAL') {
        if (this.productData.properties.animalType === 'DOG') {
          this.addAnimalUsageAreaProperty();
        } else {
          this.removeAnimalUsageAreaProperty();
        }
      }

      if (this.userConfig.isKolibriFlow && this.isSnowmobileOrTractorProductType()) {
        if (
          this.productData.properties.vehicleYoungDriver === true ||
          this.productData.properties.vehicleYoungDriver === 'true'
        ) {
          this.addDriverBirthYearProperty();
        } else {
          this.removeDriverBirthYearProperty();
        }
      }

      if (
        this.userConfig.isKolibriFlow &&
        this.isHouseOrVacationHomeProductType() &&
        this.productData.properties.hasOwnProperty(DYNAMIC_PROPERTYES.HOUSE_FIRST_RISK_SUM_INSURED.name)
      ) {
        this.addHouseFirstRiskSumInsuredProperty();
      }
    }
  }
  // Extended properties flow dynamic properties logic
}
